/**
 * Nov 4 field test (P1) — a parked car stays in the Parking Lot until it leaves.
 *
 * Founder rule: a physically parked vehicle remains in its parking spot across every later day until a pickup or a
 * location change. Its ticket's scheduled date must not decide whether the car is there.
 *
 * The Dayboard's Parking Lot list used to be built only from the selected day's tickets (`scheduled_date = day`), so
 * Ziaire's Nov 2 completed-and-parked car (spot 003, never picked up) vanished from every later day. Physical truth is
 * `vehicle_location_current` (the database keeps a completed ticket's PARKING_SPOT row until pickup). This module picks,
 * from that truth, the cars the day's own list does not already show — WITHOUT adding them to the day's schedule: no
 * duplicate ticket, no lane, no capacity. Pure: no React, no Supabase, no clock.
 */

import { isTicketDeleted } from '@/utils/ticketDeletedState';
import type { CurrentVehicleLocation, LocationKind } from './model';

/** Current-location kinds that mean "parked on the lot" (the same kinds that set `tickets.parking_lot`). */
export const PARKED_LOCATION_KINDS: readonly LocationKind[] = ['PARKING_SPOT', 'ON_SITE_GENERAL', 'ON_SITE_UNKNOWN_EXACT'];

export function isParkedLocation(loc: CurrentVehicleLocation | null | undefined): boolean {
  return !!loc && PARKED_LOCATION_KINDS.includes(loc.kind);
}

/** The chain fields that say which ticket holds a car's physical truth (server: vehicle_location_holder). */
export interface ParkedChainTicket {
  id: string;
  parent_ticket_id?: string | null;
  rollover_of_ticket_id?: string | null;
}

const holderOf = (t: ParkedChainTicket) => t.parent_ticket_id ?? t.rollover_of_ticket_id ?? t.id;

/**
 * Holder tickets whose car is parked right now and is not already drawn by `shown` (the day's own Parking Lot cards —
 * a card for the holder itself or for any ticket of its chain already represents that car).
 */
export function undrawnParkedHolderIds(
  current: ReadonlyMap<string, CurrentVehicleLocation>,
  shown: readonly ParkedChainTicket[],
): string[] {
  const drawn = new Set<string>();
  for (const t of shown) {
    drawn.add(t.id);
    drawn.add(holderOf(t));
  }
  const out: string[] = [];
  for (const loc of current.values()) {
    if (isParkedLocation(loc) && !drawn.has(loc.ticketId)) out.push(loc.ticketId);
  }
  return out.sort();
}

/**
 * Is a car that is parked NOW to be shown on `selectedDay`? Today and later: yes — it stays until it leaves, whatever
 * day its ticket is scheduled for. An earlier day: only if its ticket was scheduled on or before that day (it is still
 * here now, so it was here then; a car booked for later had not arrived).
 */
export function showsParkedOnDay(
  ticket: { scheduled_date?: string | null },
  selectedDay: string,
  today: string,
): boolean {
  if (selectedDay >= today) return true;
  return !!ticket.scheduled_date && ticket.scheduled_date <= selectedDay;
}

/**
 * The cars to list in the Parking Lot on `selectedDay` beyond the day's own cards, given the fetched holder rows.
 * Deleted / cancelled / picked-up rows are refused even if a stale location row survived.
 */
export function carriedParkedTickets<
  T extends ParkedChainTicket & {
    scheduled_date?: string | null;
    status?: string | null;
    deleted_at?: string | null;
    picked_up_at?: string | null;
  },
>(holders: readonly T[], selectedDay: string, today: string): T[] {
  return holders
    .filter((t) => !isTicketDeleted(t) && !t.picked_up_at && t.status !== 'Cancelled')
    .filter((t) => showsParkedOnDay(t, selectedDay, today))
    .sort((a, b) => (a.scheduled_date ?? '').localeCompare(b.scheduled_date ?? '') || a.id.localeCompare(b.id));
}

/** `carried` minus any car a card in `shown` already draws (the day's list can gain a chain ticket after the read). */
export function withoutDrawn<T extends ParkedChainTicket>(carried: readonly T[], shown: readonly ParkedChainTicket[]): T[] {
  const drawn = new Set<string>();
  for (const t of shown) {
    drawn.add(t.id);
    drawn.add(holderOf(t));
  }
  return carried.filter((t) => !drawn.has(t.id) && !drawn.has(holderOf(t)));
}
